import {api} from "@/shared/api";
import {FeatureModuleSelectionDialog} from "@/features/pricing/featureModuleSelectionDialog";
import {LicensedUserDialog} from "./licensedUsersDialog";
import {ExtendRuntimeDialog} from "./extendRuntimeDialog";
import * as BootstrapModal from "@/shared/ui/bootstrapModal";
import * as Button from "@/shared/utils/button";
import * as Input from "@/shared/utils/input";
import {toggleByClass} from "@/shared/utils/input";
import {formatPercent, mergeProperties} from "@/shared/utils/misc";
import {formatCurrency, formatDate, isSameOrAfterDay, parseISODate, Temporal} from "@/shared/utils/datefunctions";
import {Address, AvailableFeatureModulesResponse, FeatureModule, FeatureModulesPriceCalculationResult, RebateCode} from "@/shared/timr";
import {FeatureModuleStatus, PricingChangeType} from "@/shared/timrTypes";

enum PaymentType {
	INVOICE = "INVOICE",
	DIRECT = "DIRECT"
}

enum UpgradeType {
	FEATURE_MODULES = "FEATURE_MODULES",
	USER_LICENSES = "USER_LICENSES",
	EXTEND_RUNTIME = "EXTEND_RUNTIME"
}

type ChangeSet<T> = {
	currentState: T;
	updatedState: T;
};

interface FeatureModuleChanges {
	removed: FeatureModule[]
	added: FeatureModule[]
}

interface PricingCustomizeConfig {
	activeFeatureModules: FeatureModule[];
	activeFeatureModulesWithoutTrials: FeatureModule[];
	allFeatureModulesInConfiguration: FeatureModule[];
	userLicenses: number;
	validTo: string;
	currentDate: string;
	oldPricePerUser: number;
	oldBasePricePerMonth: number;
	company: string;
	billingAddress: Address;
	vatNumber: string;
	orderNumber: number;
	isAutofillPaymentOrderNumber: boolean;
	allowPayPerInvoice: boolean;
	isTrial: boolean;
	isFree: boolean;
	moduleChangeAllowed: boolean;
	userChangeAllowed: boolean;
	activeRebateCode?: string;
	preselectFeatureModules: FeatureModule[];
}

export class PricingCustomizationPage {

	private static readonly DEFAULT_TRIAL_RUNTIME_EXTENSION_MONTHS = 12;
	private static readonly DEFAULT_CURRENCY = "EUR";

	// ---------------------------------------
	// JQuery elements
	// ---------------------------------------

	private readonly addDiscountBtn: JQuery;
	private readonly cancelBtnHeader: JQuery;
	private readonly cancelBtnFooter: JQuery;

	private readonly btnUpgrade: JQuery;
	private readonly btnApply: JQuery;
	private payNowBtn: JQuery;

	private readonly checkoutContainer: JQuery;
	private readonly rebateContainer: JQuery;
	private readonly discountCodeContainer: JQuery;
	private readonly discountTitle: JQuery;

	private readonly rebateStatus: JQuery;
	private readonly rebateCodeInput: JQuery;
	private readonly rebateCodeInputClear: JQuery;

	// ---------------------------------------
	// Keep track of changes
	// ---------------------------------------

	private readonly changesets: Map<UpgradeType, ChangeSet<object | number>>;

	private availableFeatureModules: AvailableFeatureModulesResponse;

	private config: PricingCustomizeConfig;
	private rebateCode?: RebateCode;

	private performPaymentRequest: JQuery.jqXHR<unknown>;
	private calculateUpgradeRequest: JQuery.jqXHR<unknown>;

	// ---------------------------------------
	// Initialization
	// ---------------------------------------

	constructor(config: PricingCustomizeConfig) {
		this.config = config;

		// Initialize the map with the current state
		this.changesets = new Map<UpgradeType, ChangeSet<object>>();
		this.changesets.set(UpgradeType.FEATURE_MODULES, {
			currentState: config.activeFeatureModulesWithoutTrials,
			updatedState: config.activeFeatureModules
		});
		this.changesets.set(UpgradeType.USER_LICENSES, {
			currentState: config.userLicenses,
			updatedState: config.userLicenses
		});
		this.changesets.set(UpgradeType.EXTEND_RUNTIME, {
			currentState: 0,	// At first, no runtime extension
			updatedState: 0
		});

		// Initialize the JQuery elements
		this.addDiscountBtn = $("#addDiscountBtn");
		this.cancelBtnHeader = $("#cancelBtnHeader");
		this.cancelBtnFooter = $("#cancelBtnFooter");

		this.btnUpgrade = $("#btnUpgrade");
		this.btnApply = $("#btnApply");

		this.checkoutContainer = $("#checkoutContainer");
		this.rebateContainer = $("#rebateContainer");
		this.discountCodeContainer = $("#discountCodeContainer");
		this.discountTitle = $("#discountTitle");

		this.rebateStatus = $("[data-rebate-code-status]");
		this.rebateCodeInput = $("#rebateCodeInput");
		this.rebateCodeInputClear = $("[data-rebate-code-clear]");

		// Initialize other components
		this.initStaticContent();
		this.initListeners();
		this.loadAvailableFeatureModules();
		this.initPaymentView();
		this.initActiveRebateCode();

		if (this.isTrial()) {
			this.onExtendRuntimeSelected(PricingCustomizationPage.DEFAULT_TRIAL_RUNTIME_EXTENSION_MONTHS);
		} else if (this.isFree() && config.preselectFeatureModules?.length > 0) {
			this.onExtendRuntimeSelected(PricingCustomizationPage.DEFAULT_TRIAL_RUNTIME_EXTENSION_MONTHS);
			this.onFeatureModuleSelection(config.preselectFeatureModules, config.oldPricePerUser);
		} else {
			this.onFeatureModuleSelection(config.activeFeatureModules, config.oldPricePerUser);
		}
		this.updateArchivedFeatureModules(config.activeFeatureModules);
	}

	private initStaticContent() {
		const activeFMList = $("[data-active-feature-module-list]").empty();
		this.config.activeFeatureModules.forEach((fm: FeatureModule) => {
			activeFMList.append($("<li>").text(`${fm.name} ${fm.trialUntil ? `(${messages["admin.account.trialUntil"]} ${formatDate(parseISODate(fm.trialUntil))})` : ""}`));
		});
		$("[data-licensed-users-count]").text(this.config.userLicenses);
		$("[data-valid-to]").text(this.isFree() ? "-" : formatDate(parseISODate(this.config.validTo)));
		$("[data-current-price-per-user]").text(this.formatPrice(this.config.oldPricePerUser));
		$("[data-current-base-price-per-account]").text(this.formatPrice(this.config.oldBasePricePerMonth));
		$("[data-current-base-price-per-account-row]").toggle(!!this.config.oldBasePricePerMonth);

		// Don't show feature module btn if the client are not allowed to change
		$("[data-select-modules]").toggle(this.config.moduleChangeAllowed);

		// Don't show user upgrade btn if the client is not allowed to change
		$("[data-select-user-count]").toggle(this.config.userChangeAllowed);
	}

	private initListeners() {

		// --------------------------------------
		// Top Level Buttons
		// --------------------------------------

		$("[data-select-modules]").click(() => {
			FeatureModuleSelectionDialog.show(mergeProperties(this.availableFeatureModules, {
				activeFeatureModules: this.getUpdatedState(UpgradeType.FEATURE_MODULES) as FeatureModule[],
				oldPrice: this.config.oldPricePerUser + this.config.oldBasePricePerMonth,
				allowedChange: this.getAllowedChange(UpgradeType.FEATURE_MODULES),
				userCount: this.getUpdatedState(UpgradeType.USER_LICENSES) as number,
			}), (selectedModules, pricePerUser) => this.onFeatureModuleSelection(selectedModules, pricePerUser));
		});

		$("[data-select-user-count]").click(() => {
			LicensedUserDialog.show({
				selectedFeatureModules: this.getUpdatedState(UpgradeType.FEATURE_MODULES) as FeatureModule[],
				newUserCount: this.getUpdatedState(UpgradeType.USER_LICENSES) as number,
				allowedChange: this.getAllowedChange(UpgradeType.USER_LICENSES)
			}, (x) => this.onLicensedUserSelected(x));
		});

		$("[data-select-extend-months]").click(() => {
			ExtendRuntimeDialog.show({
				selectedFeatureModules: this.getUpdatedState(UpgradeType.FEATURE_MODULES) as FeatureModule[],
				userCount: this.getUpdatedState(UpgradeType.USER_LICENSES) as number,
				extendByMonths: this.getUpdatedState(UpgradeType.EXTEND_RUNTIME) as number,
				validTo: parseISODate(this.config.validTo),
				currentDate: parseISODate(this.config.currentDate),
				free: this.isFree()
			}, (x) => this.onExtendRuntimeSelected(x));
		});

		// --------------------------------------
		// Remove upgrade panel & reset changes
		// --------------------------------------

		$("[data-remove-change]").click((e) => {
			const target = $(e.target).closest("[data-remove-change]");
			const targetId = target.data("remove-change") as UpgradeType;
			target.closest(".panel").hide();	// Hide panel before resetting changes
			this.resetChangesForType(targetId);
			this.recalculateUpgrade();
		});

		// --------------------------------------
		// Rebate Code
		// --------------------------------------

		this.rebateCodeInput.change((e) => {
			const target = $(e.target);
			const rebateCode = target.val() as string;
			this.validateRebateCode(rebateCode)
				.always(() => this.recalculateUpgrade());
		});

		this.rebateCodeInputClear.click(() => {
			this.rebateCodeInput.val("")
				.change();
		});

		// --------------------------------------
		// Footer buttons
		// --------------------------------------

		this.btnUpgrade.click(() => this.showPaymentView());

		$("#backToConfigurationBtn").click(() => this.showConfiguration());

		this.addDiscountBtn.click(() => {
			this.addDiscountBtn.hide();
			this.rebateContainer.show();
			this.discountCodeContainer.show();
			this.discountTitle.show();
		});

		this.btnApply.click(() => this.performPayment());

		// --------------------------------------
		// Utility
		// --------------------------------------

		$("[data-accept-terms] > a").click(() => this.showTerms());

	}

	// ---------------------------------------
	// Keep track of changes
	// ---------------------------------------

	/**
	 * Set changes for a specific type
	 * @param type The type to set changes for
	 * @param _object The object to set changes to
	 * @private
	 */
	private setChangesForType(type: UpgradeType, _object: object | number): void {
		const changeSet = this.changesets.get(type);
		if (changeSet) {
			// Set to current state to not need to check every time if it is null
			changeSet.updatedState = _object;
		}
	}

	/**
	 * Reset changes for a specific type
	 * @param type The type to reset changes for
	 * @private
	 */
	private resetChangesForType(type: UpgradeType): void {
		const changeSet = this.changesets.get(type);
		if (changeSet) {
			// Set to current state to not need to check every time if it is null
			changeSet.updatedState = changeSet.currentState;
		}
		if (type === UpgradeType.EXTEND_RUNTIME) {
			// Reset the text for the extend runtime button
			$("[data-select-extend-months]").text(messages["pricing.customize.extendRuntime.label"]);
		} else if (type === UpgradeType.FEATURE_MODULES) {
			this.updateArchivedFeatureModules(this.config.activeFeatureModules);
		}
	}

	private getCurrentState(type: UpgradeType): object | number {
		const changeSet = this.changesets.get(type);
		return changeSet?.currentState;
	}

	private getUpdatedState(type: UpgradeType): object | number {
		const changeSet = this.changesets.get(type);
		return changeSet?.updatedState;
	}

	private hasChanges(type: UpgradeType): boolean {
		switch (type) {
			case UpgradeType.FEATURE_MODULES:
				return this.hasFeatureModuleChanges();
			case UpgradeType.USER_LICENSES:
				return this.getCurrentState(type) !== this.getUpdatedState(type);
			case UpgradeType.EXTEND_RUNTIME:
				return this.getCurrentState(type) !== this.getUpdatedState(type);
			default:
				return false;
		}
	}

	// ---------------------------------------
	// Feature Modules Helpers
	// ---------------------------------------

	private loadAvailableFeatureModules(): void {
		void api("pricing/getAvailableFeatureModules.json")
			.get()
			.done((response: AvailableFeatureModulesResponse) => {
				this.availableFeatureModules = response;
			});
	}

	private canFeatureModuleBeExtended(featureModule: FeatureModule): boolean {
		switch (featureModule.status) {
			case FeatureModuleStatus.MAIN:
			case FeatureModuleStatus.LEGACY:
			case FeatureModuleStatus.PRIVATE:
			case FeatureModuleStatus.EXPERIMENTAL:
				return true;
			case FeatureModuleStatus.DISCONTINUED:
			case FeatureModuleStatus.IAP:
			case FeatureModuleStatus.DRAFT:
				return false;
		}
	}

	private updateArchivedFeatureModules(activeFeatureModules: FeatureModule[]) {
		if (activeFeatureModules?.some(fm => !this.canFeatureModuleBeExtended(fm))) {
			const activeFeatureModulesExpandable = activeFeatureModules?.filter(fm => this.canFeatureModuleBeExtended(fm));
			this.onFeatureModuleSelection(activeFeatureModulesExpandable, activeFeatureModulesExpandable.reduce((sum, fm) => sum + fm.pricePerUser, 0));
		}
	}

	/**
	 * Find the additional and removed feature modules between the current and updated state
	 * @private
	 */
	private findDifferenceInFeatureModules(): FeatureModuleChanges {
		const changeSet = this.changesets.get(UpgradeType.FEATURE_MODULES);
		if (!changeSet) {
			return;
		}
		const currentState = changeSet.currentState as FeatureModule[];
		const updatedState = changeSet.updatedState as FeatureModule[];

		const additionalFeatureModules = updatedState.filter((updatedFeatureModule) => !currentState.some((currentFeatureModule) => currentFeatureModule.id === updatedFeatureModule.id));
		const removedFeatureModules = currentState.filter((currentFeatureModule) => !updatedState.some((updatedFeatureModule) => updatedFeatureModule.id === currentFeatureModule.id));
		return {
			added: additionalFeatureModules,
			removed: removedFeatureModules
		}
	}

	/**
	 * Check if there are changes in the feature modules
	 * @private
	 */
	private hasFeatureModuleChanges(): boolean {
		const difference = this.findDifferenceInFeatureModules();
		return difference.added.length > 0 || difference.removed.length > 0;
	}

	/**
	 * Fill the feature module upgrade panel with the changes
	 * @private
	 */
	private updateFeatureModuleUpgradePanel(changedFeatureModules: FeatureModuleChanges): void {
		const updatedList = $("[data-updated-feature-module-list]").empty();
		for (const fm of changedFeatureModules.removed) {
			const liElement = $("<li>").addClass("list-remove").text(fm.name);
			if (!this.canFeatureModuleBeExtended(fm)) {
				liElement.addClass("text-danger")
					.append($("<i>").addClass("ml-20 mr-5 far fa-exclamation-triangle"))
					.append($("<span>").text(messages["pricing.customize.modules.notAvailable"]));
			}
			updatedList.append(liElement);
		}
		for (const fm of changedFeatureModules.added) {
			updatedList.append($("<li>").addClass("list-add").text(fm.name));
		}
		$("[data-updated-feature-module-list-header]")
			.text(
				changedFeatureModules.added?.length > changedFeatureModules.removed?.length ?
					messages["pricing.customize.modules.added"] :
					messages["pricing.customize.modules.removed"]
			);
	}

	private updateFeatureModulePrice(calculationResult: FeatureModulesPriceCalculationResult): void {
		const newBasePrice = calculationResult.summary.basePrice;
		const newPricePerUser = calculationResult.summary.pricePerUser;
		const featureModuleUpgradeFee = calculationResult.upgradeSummary.upgradeFees.featureModuleUpgradeFee;
		$("[data-old-price]").text(this.formatPrice(this.config.oldPricePerUser));
		$("[data-old-price-info]").text(this.formatFeatureModulePriceInfo(this.config.oldBasePricePerMonth));
		$("[data-new-price]").text(this.formatPrice(newPricePerUser));
		$("[data-new-price-info]").text(this.formatFeatureModulePriceInfo(newBasePrice));
		$("[data-fm-price]")
			.toggle(featureModuleUpgradeFee > 0)
			.text(this.formatPrice(featureModuleUpgradeFee));
		$("[data-modules-price-from-to]")
			.toggle(featureModuleUpgradeFee > 0)
			.text(messages.withArguments("pricing.customize.modules.upgrade", formatDate(Temporal.Now.plainDateISO()), formatDate(parseISODate(this.config.validTo))));
		this.updateShowUpgradeOrDowngrade(
			$('[data-change-panel="FEATURE_MODULES"]'),
			newPricePerUser + newBasePrice >= this.config.oldPricePerUser + this.config.oldBasePricePerMonth
		);
	}

	// ---------------------------------------
	// User Licences Helpers
	// ---------------------------------------

	private getLicensesDifference(): number {
		return (this.getUpdatedState(UpgradeType.USER_LICENSES) as number) - (this.getCurrentState(UpgradeType.USER_LICENSES) as number);
	}

	private updateUserLicensesPanel() {
		const licensesDifference = this.getLicensesDifference();
		const validTo = parseISODate(this.config.validTo);
		$("[data-upgrade-licenses-count]").text(
			messages.withArguments(`pricing.customize.licenses.${licensesDifference > 0 ? "added" : "removed"}`, licensesDifference)
		);
		$("[data-licenses-price-from-to]").text(messages.withArguments("pricing.customize.licenses.upgrade", formatDate(Temporal.Now.plainDateISO()), formatDate(validTo)));
		this.updateShowUpgradeOrDowngrade($('[data-change-panel="USER_LICENSES"]'), licensesDifference > 0);
	}

	private updateLicensesPrice(calculationResult: FeatureModulesPriceCalculationResult): void {
		const userUpgradeFee = calculationResult.upgradeSummary.upgradeFees.userUpgradeFee;
		$("[data-licenses-price-from-to]").toggle(userUpgradeFee > 0)
		$("[data-licenses-price]")
			.toggle(userUpgradeFee > 0)
			.text(this.formatPrice(userUpgradeFee));
	}

	// ---------------------------------------
	// Extend Runtime Helpers
	// ---------------------------------------

	private updateExtendRuntimePanel(extendByMonths: number) {
		const validTo = parseISODate(this.config.validTo);
		const currentDate = parseISODate(this.config.currentDate);
		const newRuntimeValidFrom = isSameOrAfterDay(validTo, currentDate) ? validTo : currentDate;
		const newValidTo = newRuntimeValidFrom?.add({months: extendByMonths});
		$("[data-extend-runtime-to]").text(messages.withArguments("pricing.customize.newRuntimeValidTo", formatDate(newValidTo)));
		$("[data-extend-runtime-months]").html(messages.withArguments("pricing.customize.extendedBy", extendByMonths));
	}

	private updateExtendedRuntimePrice(calculationResult: FeatureModulesPriceCalculationResult): void {
		$("[data-extended-runtime-price]").text(this.formatPrice(calculationResult.upgradeSummary.upgradeFees.runtimeExtensionFee));
	}

	// ---------------------------------------
	// Callback Handlers
	// ---------------------------------------

	/**
	 * Event handler for the feature module selection dialog
	 * @param selectedFeatureModules The selected feature modules
	 * @param pricePerUser			 The new price per user
	 * @private
	 */
	private onFeatureModuleSelection(selectedFeatureModules: FeatureModule[], pricePerUser: number) {
		this.setChangesForType(UpgradeType.FEATURE_MODULES, selectedFeatureModules);
		this.updateFeatureModuleUpgradePanel(this.findDifferenceInFeatureModules());
		// Decide if user change is allowed based on the selected feature modules
		const userChangeAllowed = !selectedFeatureModules.some(fm => fm.fixedNumberOfUsers);
		$("[data-select-user-count]").toggle(userChangeAllowed);
		if (this.isTrial()) {
			this.config = {
				...this.config,
				oldPricePerUser: pricePerUser,
				activeFeatureModules: selectedFeatureModules,
				userChangeAllowed: userChangeAllowed
			}
		}
		// Revalidate the rebate code & toggle the upgrade panel => recalculate upgrade
		this.validateRebateCode(this.rebateCodeInput.val() as string)
			.always(() => this.toggleUpgradePanel(UpgradeType.FEATURE_MODULES));
	}

	private onLicensedUserSelected(licensesCount: number) {
		this.setChangesForType(UpgradeType.USER_LICENSES, licensesCount);
		this.updateUserLicensesPanel();
		if (this.isTrial()) {
			this.config = {
				...this.config,
				userLicenses: licensesCount
			}
		}
		this.toggleUpgradePanel(UpgradeType.USER_LICENSES);
	}

	private onExtendRuntimeSelected(extendBy: number) {
		this.setChangesForType(UpgradeType.EXTEND_RUNTIME, extendBy);
		this.updateExtendRuntimePanel(extendBy);
		$("[data-select-extend-months]").text(messages["pricing.customize.changeRuntime.label"]);
		this.toggleUpgradePanel(UpgradeType.EXTEND_RUNTIME);
	}

	// ---------------------------------------
	// Recalculate Steps & Total Price
	// ---------------------------------------

	private recalculateUpgrade() {
		if (this.hasChanges(UpgradeType.FEATURE_MODULES) || this.hasChanges(UpgradeType.USER_LICENSES) || this.hasChanges(UpgradeType.EXTEND_RUNTIME)) {
			this.calculateUpgradeRequest?.abort();
			this.calculateUpgradeRequest = api("pricing/calculateUpgrade.json")
				.get({
					users: this.getUpdatedState(UpgradeType.USER_LICENSES) as number,
					months: this.getUpdatedState(UpgradeType.EXTEND_RUNTIME) as number,
					selectedModuleIds: this.getSelectedFeatureModuleIds(),
					rebateCode: this.rebateCode?.code
				})
				.done((result: FeatureModulesPriceCalculationResult) => {
					const hasPrice = result.totalAmountBeforeVAT > 0;
					this.btnUpgrade.toggle(hasPrice);
					this.btnApply.toggle(!this.isTrial() && !hasPrice);
					toggleByClass(this.btnApply, this.isTrial());

					this.rebateContainer.toggle(hasPrice || !!this.config.activeRebateCode);
					this.discountCodeContainer.showIf(!!this.config.activeRebateCode);
					// Show only if at least one upgrade/downgrade panel is visible =>
					// Ensures that in trial no checkout is shown, except if there's an extension
					const showCheckoutContainer = $("[data-change-panel]").is(":visible");
					this.checkoutContainer.toggle(showCheckoutContainer);
					this.cancelBtnFooter.toggle(this.isTrial() && !hasPrice);
					this.cancelBtnHeader.toggle(!this.isTrial() || hasPrice);
					this.addDiscountBtn.toggle(showCheckoutContainer && !this.config.activeRebateCode);

					this.updateFeatureModulePrice(result);
					this.updateLicensesPrice(result);
					this.updateExtendedRuntimePrice(result);
					this.updateTotalPriceView(result);
				});
		} else {
			this.btnUpgrade.hide();
			this.btnApply.hide();

			this.checkoutContainer.hide();
			this.cancelBtnFooter.show();
			this.cancelBtnHeader.hide();
			this.addDiscountBtn.hide();
			if (!this.config.activeRebateCode) {
				this.discountCodeContainer.hide();
				this.discountTitle.hide();
			}
		}
	}

	/**
	 * Toggle the upgrade panel based on the changes
	 *
	 * Special: If it is in trial only show the panel for EXTEND_RUNTIME
	 * Others will update the static context with the new updated changes (TIMR-8940)
	 */
	private toggleUpgradePanel(type: UpgradeType) {
		const panel = $(`[data-change-panel="${type}"]`);
		if (type === UpgradeType.EXTEND_RUNTIME) {
			panel.toggle(this.hasChanges(type));
		} else if (this.isTrial()) {
			this.initStaticContent();
		} else {
			panel.toggle(this.hasChanges(type));
		}
		this.recalculateUpgrade();
	}

	private updateShowUpgradeOrDowngrade(panel: JQuery, isUpgrade: boolean) {
		panel
			.removeClass("panel-upgrade panel-downgrade")
			.addClass(isUpgrade ? "panel-upgrade" : "panel-downgrade");
		panel.find("[data-panel-title]").text(isUpgrade ? messages["pricing.customize.upgrade"] : messages["pricing.customize.downgrade"]);
		panel.find("[data-text-upgrade]").toggle(isUpgrade);
		panel.find("[data-text-downgrade]").toggle(!isUpgrade);
	}

	private updateTotalPriceView(calculationResult: FeatureModulesPriceCalculationResult) {
		const hasVatPercentage = calculationResult.vatPercentage != null;	// no !== to check for undefined or null
		$("[data-vat-label]").text(`${hasVatPercentage ? formatPercent(calculationResult.vatPercentage, 0) : ""} ${messages["pricing.customize.totalPrice.vat"]}`);
		$("[data-rebate-text]").text(messages.withArguments("pricing.customize.rebate", formatPercent(this.rebateCode?.rebate?.discountPercentage)));
		$("[data-rebate-amount]").text(this.formatPrice(calculationResult.upgradeSummary.upgradeFees.upgradeRebateDiscount));
		$("[data-total-amount-excluding-vat]").text(this.formatPrice(calculationResult.totalAmountBeforeVAT));
		$("[data-amount-vat]").text(hasVatPercentage ? this.formatPrice(calculationResult.totalAmountVAT ?? 0) : messages["pricing.customize.reverseCharge"]);
		$("[data-total-amount-including-vat]").text(this.formatPrice(calculationResult.totalAmountPayable || calculationResult.totalAmountBeforeVAT));

		const userVolumeDiscountAvailable = !!calculationResult.upgradeSummary.upgradeFees.upgradeUserVolumeDiscount;
		$("[data-user-volume-discount-container]").toggle(userVolumeDiscountAvailable);
		$("[data-user-volume-discount-text]").text(messages.withArguments("pricing.customize.userVolumeDiscount", formatPercent(calculationResult.summary.userVolumeDiscountPercentage)));
		$("[data-user-volume-discount-amount]").text(this.formatPrice(calculationResult.upgradeSummary.upgradeFees.upgradeUserVolumeDiscount));

		$("[data-runtime-discount-container]").toggle(!!calculationResult.summary.runtimeDiscount);
		$("[data-runtime-discount-text]").text(messages.withArguments("pricing.customize.runtimeDiscount", formatPercent(calculationResult.summary.runtimeDiscountPercentage)));
		$("[data-runtime-discount-amount]").text(this.formatPrice(calculationResult.summary.runtimeDiscount));

		// Only show "Discounts" if there are any discounts available
		this.discountTitle.toggle(this.discountCodeContainer.is(":visible") || userVolumeDiscountAvailable);
	}

	// ---------------------------------------
	// Rebates
	// ---------------------------------------

	private initActiveRebateCode() {
		if (this.config.activeRebateCode) {
			this.rebateCodeInput.val(this.config.activeRebateCode)
				.change();
			this.rebateContainer.show();
			this.discountCodeContainer.show();
		}
	}

	/**
	 * Validates the provided rebate code and updates the UI based on its validity.
	 *
	 * @param {string} rebateCode - The rebate code to be validated.
	 */
	private validateRebateCode(rebateCode: string): JQueryPromise<boolean> {
		const deferred = $.Deferred<boolean>();

		this.rebateStatus.empty().removeClass().addClass("icon-spinner2 spinner");
		this.rebateCode = null;
		const rebateNameElement = $("[data-rebate-name]");
		rebateNameElement.html("").removeClass("error-text");
		if (rebateCode) {
			void api("account/payment/validateRebateCode.json")
				.post({
					rebateCode: rebateCode,
					featureModuleIds: this.getSelectedFeatureModuleIds()
				})
				.done(data => {
					if (data.rebateCode && data.valid) {
						this.rebateCode = data.rebateCode;
						this.showRebateCode(data.rebateCode);
					} else {
						rebateNameElement.show()
							.text(data.message || messages["admin.payment.rebateCode.invalid"])
							.closest("div.flex-row")
							.removeClass("has-success")
							.addClass("has-error");
						$("[data-rebate-code-status]").removeClass().addClass("icon-blocked");
						$("[data-rebate-container]").hide();
					}
					deferred.resolve(data.valid);
				});
		} else {
			$("[data-rebate-container]").hide();
			this.rebateCodeInputClear.hide();
			this.rebateStatus.removeClass().addClass("far fa-question");
			rebateNameElement.hide()
				.closest("div.flex-row")
				.removeClass("has-error")
				.removeClass("has-success");
			deferred.resolve(false);
		}

		return deferred.promise();
	}

	private showRebateCode(rebateCode: RebateCode) {
		const rebateName = $("[data-rebate-name]").empty();
		rebateName.text(rebateCode.rebate.name)
			.show()
			.closest("div.flex-row")
			.removeClass("has-error")
			.addClass("has-success");
		this.rebateStatus.removeClass().addClass("icon-checkmark");
		$("[data-rebate-container]").show();
		this.rebateCodeInputClear.show();
	}

	// ---------------------------------------
	// Payment
	// ---------------------------------------

	private initPaymentView() {
		const billingAddressSummary = $("[data-billing-address-summary]").empty();
		billingAddressSummary.append($("<p></p>").text(this.config.company));
		if (this.config.billingAddress) {
			billingAddressSummary.append($("<p></p>").text(this.config.billingAddress.address))
				.append($("<p></p>").text(this.config.billingAddress.zipCode + " " + this.config.billingAddress.city))
				.append($("<p></p>").text(this.config.billingAddress.state))
				.append($("<p></p>").text(this.config.billingAddress.country.name));
		}
		if (this.config.billingAddress.country && this.config.vatNumber) {
			billingAddressSummary.append($("<div></div>", {"class": "clean"}))
				.append($("<p></p>").text(messages["admin.account.vatNumber"] + " " + this.config.billingAddress.country.code + " " + this.config.vatNumber));
		}

		$("#orderNumberInput").val(this.config.orderNumber);
		$("#autoFillLastOrderNumberCheckbox").prop("checked", this.config.isAutofillPaymentOrderNumber);

		const defaultPaymentType = this.config.allowPayPerInvoice ? PaymentType.INVOICE : PaymentType.DIRECT;

		this.payNowBtn = $("#payNowBtn");
		const acceptTermsCheckbox = $("#acceptTerms");
		Input.toggle(this.payNowBtn, acceptTermsCheckbox.is(":checked"));
		this.payNowBtn.text(messages["pricing.customize.payNow." + defaultPaymentType]);
		this.payNowBtn.click(() => {
			this.performPayment();
		});

		$("#paymentTypeInvoice").toggle(this.config.allowPayPerInvoice);

		$("[name='selectedPaymentType']").change((e) => {
			const paymentType = $(e.target).val() as PaymentType;
			this.payNowBtn.text(messages["pricing.customize.payNow." + paymentType]);
		});
		$(`[name='selectedPaymentType'][value=${PaymentType.INVOICE}]`).prop("checked", defaultPaymentType === PaymentType.INVOICE);
		$(`[name='selectedPaymentType'][value=${PaymentType.DIRECT}]`).prop("checked", defaultPaymentType === PaymentType.DIRECT);

		acceptTermsCheckbox.change((e) => {
			const target = $(e.target);
			Input.toggle(this.payNowBtn, target.is(":checked"));
		});
	}

	private showPaymentView() {
		$("#panelConfiguration").hide();
		$("#panelPayment").fadeIn();

		window.scrollTo({behavior: "smooth", top: 0});
	}

	private performPayment() {
		Button.setLoading(this.payNowBtn);
		Button.setLoading(this.btnApply);

		this.performPaymentRequest?.abort();
		this.performPaymentRequest = api("account/payment/performPayment.json")
			.post({
				numberOfUsers: this.getUpdatedState(UpgradeType.USER_LICENSES) as number,
				numberOfMonths: this.getUpdatedState(UpgradeType.EXTEND_RUNTIME) as number,
				featureModuleIds: this.getSelectedFeatureModuleIds(),
				rebateCode: this.rebateCode?.code,
				orderNumber: $("#orderNumberInput").val() as string,
				autoFillPaymentOrderNumber: $("[name=autoFillPaymentOrderNumber]").is(":checked"),
				paymentType: $("[name='selectedPaymentType']:checked").val() as PaymentType,
				acceptTerms: $("#acceptTerms").is(":checked")
			})
			.done((result: { redirectLocation: string }) => {
				window.location.href = result.redirectLocation;
			})
			.always(() => {
				Button.resetLoading(this.payNowBtn);
				Button.resetLoading(this.btnApply);
			});
	}

	private getSelectedFeatureModuleIds(): string {
		// Use toString() here to prevent the array from not being sent as a parameter
		const ids = (<FeatureModule[]>this.getUpdatedState(UpgradeType.FEATURE_MODULES)).map((fm) => fm.id);
		return ids.toString();
	}

	private showConfiguration() {
		$("#panelPayment").hide();
		$("#panelConfiguration").fadeIn();
		window.scrollTo({behavior: "smooth", top: 0});
	}

	private showTerms() {
		new BootstrapModal.Modal({
			title: "terms.title",
			titleSize: "h1",
			message: $("#termsModal"),
			size: "modal-lg",
			destroyOnClose: false,
			buttons: [{
				text: "dialog.print",
				action: function () {
					const termsWindow = window.open("../terms.html");
					termsWindow.print();
				}
			}, {
				text: "dialog.close",
				css: "btn-primary"
			}]
		}).show();
	}

	// ----------------------
	// Helper methods
	// ----------------------

	/**
	 * Format a price to a string with a default currency
	 * @param amount The amount to format
	 */
	private formatPrice(amount: number): string {
		return formatCurrency(amount, PricingCustomizationPage.DEFAULT_CURRENCY);
	}

	private formatFeatureModulePriceInfo(basePrice: number): string {
		if (basePrice) {
			return `${messages["pricing.priceInfo.plusPerUser"]} + ${this.formatPrice(basePrice)} ${messages["pricing.priceInfo.basePrice"]}`;
		} else {
			return messages["pricing.priceInfo.plusPerUser"];
		}
	}

	private isTrial() {
		return this.config.isTrial;
	}

	private isFree() {
		return this.config.isFree;
	}

	/**
	 * Returns NONE if feature modules and users haven't changed
	 * Return UPGRADE if one of the two is upgraded
	 * Return DOWNGRADE if one of the two is downgraded
	 */
	private getAllowedChange(type: UpgradeType): PricingChangeType {
		if (this.isTrial()) {
			// Doesn't matter in trial
			return PricingChangeType.NONE;
		}
		const featureModuleChange = this.getChange(UpgradeType.FEATURE_MODULES);
		const userChange = this.getChange(UpgradeType.USER_LICENSES);
		if ((featureModuleChange === PricingChangeType.NONE && userChange === PricingChangeType.NONE) ||
			(type === UpgradeType.FEATURE_MODULES && userChange === PricingChangeType.NONE) ||
			(type === UpgradeType.USER_LICENSES && featureModuleChange === PricingChangeType.NONE)) {
			return PricingChangeType.NONE;
		}
		if (featureModuleChange === PricingChangeType.UPGRADE || userChange === PricingChangeType.UPGRADE) {
			return PricingChangeType.UPGRADE;
		}
		return PricingChangeType.DOWNGRADE;
	}

	private getChange(type: UpgradeType): PricingChangeType {
		const panel = $(`[data-change-panel="${type}"]`);
		if (panel.is(":visible")) {
			return panel.hasClass("panel-upgrade") ? PricingChangeType.UPGRADE : PricingChangeType.DOWNGRADE;
		}
		return PricingChangeType.NONE;
	}
}
